/**
 * Chord Editor Validation Utilities
 *
 * This module provides validation functions for the chord editor UI.
 * These functions determine which chord options (qualities, extensions, alterations, adds)
 * should be disabled based on the current chord configuration.
 */
import { getNoteIndex, normalizeAlterationSymbol } from './enharmonicUtils.js';
import { getChordNotes, getCompositeQualityString } from './chordNotes.js';
// ============================================================================
// Constants
// ============================================================================
/**
 * Semitone intervals for add options.
 * Maps add values to their semitone offset from the root note.
 */
export const ADD_INTERVALS = {
    add2: 2, // Major 2nd
    add4: 5, // Perfect 4th
    add6: 9, // Major 6th
    add9: 14, // Major 9th (octave + 2)
    add11: 17, // Perfect 11th (octave + 5)
    add13: 21, // Major 13th (octave + 9)
};
// ============================================================================
// Validation Functions
// ============================================================================
/**
 * Determines which add options should be disabled based on the chord's base notes.
 *
 * **Music Theory Rule:**
 * An add is disabled if it would duplicate a note already present in the base chord.
 * For example, a C major 9 chord contains C, E, G, B, and D. Since D is already in
 * the chord (as the 9th), add9 would be redundant and should be disabled.
 *
 * **Algorithm:**
 * 1. Calculate the base chord notes (root, third, fifth, seventh, extensions)
 * 2. For each add option (add2, add4, add6, add9, add11, add13):
 *    - Calculate the note that would be added using semitone intervals
 *    - If that note is already in the base chord, disable the add
 *
 * **Semitone Intervals:**
 * - add2: +2 semitones (major 2nd)
 * - add4: +5 semitones (perfect 4th)
 * - add6: +9 semitones (major 6th)
 * - add9: +14 semitones (major 9th, octave + 2)
 * - add11: +17 semitones (perfect 11th, octave + 5)
 * - add13: +21 semitones (major 13th, octave + 9)
 *
 * @param fundamental - The root note of the chord. Accepts both ASCII (C, F#, Bb)
 *                      and Unicode (C, F♯, B♭) accidentals. Enharmonic equivalents
 *                      (F♯ vs G♭) produce identical results.
 * @param quality - The chord quality from the chord qualities data file. Valid values
 *                  include "major", "minor", "diminished", "augmented", "sus2", "sus4",
 *                  "minorMajor".
 * @param extension - The chord extension from the chord extensions data file. Valid
 *                    values include "-" (no extension), "6", "7", "9", "11", "13".
 * @param isDominant - Whether the chord is marked as dominant. Dominant chords have
 *                     a major third and minor seventh (e.g., G7 in the key of C).
 * @param alterations - Array of alteration values from the chord alterations data file.
 *                      Values are Unicode symbols ("♭5", "♯5", "♭9", "♯9", "♯11", "♭13");
 *                      ASCII spellings ("b5", "#9") are accepted and normalized.
 *                      Use empty array or ["-"] for no alterations.
 *
 * @returns A Set containing the add values that should be disabled in the UI. Returns
 *          an empty Set if no adds conflict with the base chord. Returns an empty Set
 *          if the fundamental note is invalid.
 *
 * @example
 * // F major chord contains F, A, C (indices 5, 9, 0)
 * // add4 = Bb (index 10) - NOT in base chord, so allowed
 * // add6 = D (index 2) - NOT in base chord, so allowed
 * getDisabledAdds("F", "major", "-", false, [])
 * // Returns: Set([]) - no adds are disabled
 *
 * @example
 * // C major 9 chord contains C, E, G, B, D (indices 0, 4, 7, 11, 2)
 * // add9 from C = D (index 2) - already in chord via extension
 * getDisabledAdds("C", "major", "9", false, [])
 * // Returns: Set(["add9"]) - add9 is disabled because D is already present
 *
 * @example
 * // Enharmonic equivalents produce identical results
 * getDisabledAdds("F♯", "major", "-", false, [])
 * getDisabledAdds("G♭", "major", "-", false, [])
 * // Both return the same disabled set
 *
 * @example
 * // Invalid fundamental returns empty set
 * getDisabledAdds("InvalidNote", "major", "-", false, [])
 * // Returns: Set([]) - gracefully handles invalid input
 */
export function getDisabledAdds(fundamental, quality, extension, isDominant, alterations) {
    // Get fundamental note index
    const fundamentalIndex = getNoteIndex(fundamental);
    // Handle invalid fundamental - return empty Set
    if (fundamentalIndex === -1) {
        return new Set();
    }
    // Build ParsedChordData object
    const chordData = {
        fundamental,
        quality,
        extension,
        isDominant,
        adds: [], // Don't include adds when calculating base chord notes
        alterations: alterations.filter(a => a !== "-"),
        bass: null
    };
    // Get composite quality string
    const compositeQuality = getCompositeQualityString(chordData);
    // Get base chord notes (without adds)
    const baseChordNotes = getChordNotes({
        fundamental,
        quality: compositeQuality,
        extension: null,
        isDominant: false,
        adds: [],
        alterations: chordData.alterations,
        bass: null,
    });
    // Convert base chord notes to Set of note indices
    const baseChordNoteIndices = new Set();
    for (const noteName of baseChordNotes) {
        const noteIndex = getNoteIndex(noteName);
        if (noteIndex !== -1) {
            baseChordNoteIndices.add(noteIndex);
        }
    }
    // Check each add to see if it conflicts with base chord notes
    const disabledAdds = new Set();
    for (const [addValue, semitoneOffset] of Object.entries(ADD_INTERVALS)) {
        // Calculate the add note index using semitone offset
        const addNoteIndex = (fundamentalIndex + semitoneOffset) % 12;
        // If the add note index is in the base chord set, mark it as disabled
        if (baseChordNoteIndices.has(addNoteIndex)) {
            disabledAdds.add(addValue);
        }
    }
    return disabledAdds;
}
/**
 * Determines which alteration options should be disabled based on the chord context.
 *
 * **Music Theory Rules:**
 *
 * 1. **Prerequisite Requirements:**
 *    - b9 and #9 alterations require a 7th to be present in the chord
 *    - #11 and b13 alterations require a 9th to be present in the chord
 *    - Rationale: You can't alter a note that isn't in the chord
 *
 * 2. **Quality-Based Restrictions:**
 *    - Diminished chords already have a b5, so b5 and #5 alterations are redundant
 *    - Augmented chords already have a #5, so b5 and #5 alterations are redundant
 *
 * 3. **Mutual Exclusions:**
 *    - b9 and #9 are mutually exclusive (can't flatten and sharpen the same note)
 *    - b5 and #5 are mutually exclusive (can't flatten and sharpen the same note)
 *
 * **Seventh Detection:**
 * A chord has a seventh if:
 * - isDominant is true (dominant chords always have a minor 7th), OR
 * - extension is "7", "9", "11", or "13" (these all include a 7th)
 *
 * **Ninth Detection:**
 * A chord has a ninth if:
 * - extension is "9", "11", or "13" (these all include a 9th)
 *
 * @param quality - The chord quality from the chord qualities data file. Valid values
 *                  include "major", "minor", "diminished", "augmented", "sus2", "sus4",
 *                  "minorMajor".
 * @param extension - The chord extension from the chord extensions data file. Valid
 *                    values include "-" (no extension), "6", "7", "9", "11", "13".
 * @param isDominant - Whether the chord is marked as dominant. Dominant chords have
 *                     a major third and minor seventh.
 * @param currentAlterations - Array of currently selected alterations. Used to enforce
 *                             mutual exclusion rules. Values are the Unicode symbols
 *                             from chord-alterations.json ("♭5", "♯5", "♭9", "♯9",
 *                             "♯11", "♭13"); ASCII spellings ("b5", "#9") are accepted
 *                             and normalized. Use empty array or ["-"] for no alterations.
 *
 * @returns A Set containing the alteration values that should be disabled in the UI,
 *          using the Unicode symbols from chord-alterations.json ("♭9", "♯11", ...).
 *          Returns an empty Set if all alterations are allowed (rare - usually some
 *          are disabled based on prerequisites or quality restrictions).
 *
 * @example
 * // Major chord with no extension - ♭9 and ♯9 require a 7th, ♯11 and ♭13 require a 9th
 * getDisabledAlterations("major", "-", false, [])
 * // Returns: Set(["♭9", "♯9", "♯11", "♭13"])
 *
 * @example
 * // Dominant 7 chord - has 7th (so ♭9/♯9 allowed), but no 9th (so ♯11/♭13 disabled)
 * getDisabledAlterations("major", "7", true, [])
 * // Returns: Set(["♯11", "♭13"])
 *
 * @example
 * // Major 9 chord - has both 7th and 9th, so all alterations allowed
 * getDisabledAlterations("major", "9", false, [])
 * // Returns: Set([]) - all alterations available
 *
 * @example
 * // Diminished chord - ♭5 and ♯5 disabled because diminished already has ♭5
 * getDisabledAlterations("diminished", "7", false, [])
 * // Returns: Set(["♭5", "♯5"])
 *
 * @example
 * // Augmented chord - ♭5 and ♯5 disabled because augmented already has ♯5
 * getDisabledAlterations("augmented", "-", false, [])
 * // Returns: Set(["♭5", "♯5", "♭9", "♯9", "♯11", "♭13"]) - quality + prerequisite rules
 *
 * @example
 * // Mutual exclusion - if ♭9 is selected, ♯9 is disabled
 * getDisabledAlterations("major", "9", false, ["♭9"])
 * // Returns: Set(["♯9"]) - ♯9 disabled because ♭9 is selected
 *
 * @example
 * // Mutual exclusion - if ♯5 is selected, ♭5 is disabled
 * getDisabledAlterations("major", "7", true, ["♯5"])
 * // Returns: Set(["♭5", "♯11", "♭13"]) - ♭5 disabled by mutual exclusion, others by prerequisites
 */
export function getDisabledAlterations(quality, extension, isDominant, currentAlterations) {
    const disabled = new Set();
    // Determine if chord has a seventh
    const hasSeventh = isDominant || ["7", "9", "11", "13"].includes(extension);
    // Determine if chord has a ninth
    const hasNinth = ["9", "11", "13"].includes(extension);
    // If no seventh, disable ♭9 and ♯9
    if (!hasSeventh) {
        disabled.add("♭9");
        disabled.add("♯9");
    }
    // If no ninth, disable ♯11 and ♭13
    if (!hasNinth) {
        disabled.add("♯11");
        disabled.add("♭13");
    }
    // Diminished already has a ♭5, augmented already has a ♯5 - fifth
    // alterations are redundant for both
    if (quality === "diminished" || quality === "augmented") {
        disabled.add("♭5");
        disabled.add("♯5");
    }
    // Apply mutual exclusions based on currentAlterations
    const currentAlterationsSet = new Set(currentAlterations.filter(a => a !== "-").map(normalizeAlterationSymbol));
    // ♭9/♯9 and ♭5/♯5 are mutually exclusive pairs
    if (currentAlterationsSet.has("♭9")) {
        disabled.add("♯9");
    }
    if (currentAlterationsSet.has("♯9")) {
        disabled.add("♭9");
    }
    if (currentAlterationsSet.has("♭5")) {
        disabled.add("♯5");
    }
    if (currentAlterationsSet.has("♯5")) {
        disabled.add("♭5");
    }
    return disabled;
}
/**
 * Determines which quality options should be disabled based on dominant state.
 *
 * **Music Theory Rule:**
 * Dominant chords are major-based by definition in jazz and popular music theory.
 * The term "dominant" refers to the V chord in a key, which is always a major chord
 * with a minor seventh (e.g., G7 in the key of C major).
 *
 * When a chord is marked as dominant:
 * - It must have a major third (so minor, diminished qualities are incompatible)
 * - It cannot be augmented (augmented is a separate chord type)
 * - It cannot be minorMajor (this is a minor chord with major 7th)
 *
 * **Allowed Qualities for Dominant Chords:**
 * - major (standard dominant quality)
 * - sus2 (suspended 2nd, replaces the third)
 * - sus4 (suspended 4th, replaces the third)
 *
 * **Disallowed Qualities for Dominant Chords:**
 * - minor (has minor third, conflicts with dominant's major third)
 * - minorMajor (minor chord type, incompatible with dominant function)
 * - diminished (has minor third and diminished fifth, incompatible)
 * - augmented (different chord type with augmented fifth)
 *
 * @param isDominant - Whether the chord is marked as dominant. When true, the chord
 *                     functions as a V chord (dominant) in a key, requiring a major
 *                     third and minor seventh.
 *
 * @returns A Set containing the quality values that should be disabled in the UI.
 *          Returns an empty Set if isDominant is false (all qualities allowed).
 *          Returns Set(["minor", "minorMajor", "diminished", "augmented"]) if
 *          isDominant is true.
 *
 * @example
 * // Non-dominant chord - all qualities allowed
 * getDisabledQualities(false)
 * // Returns: Set([])
 *
 * @example
 * // Dominant chord - only major and sus qualities allowed
 * getDisabledQualities(true)
 * // Returns: Set(["minor", "minorMajor", "diminished", "augmented"])
 *
 * @example
 * // Typical usage in chord editor: G7 (dominant) in key of C
 * // User marks chord as dominant, so minor/diminished/augmented are disabled
 * const disabled = getDisabledQualities(true);
 * console.log(disabled.has("minor")); // true - can't have Gm7 as dominant
 * console.log(disabled.has("major")); // false - G7 (major) is allowed
 * console.log(disabled.has("sus4")); // false - G7sus4 is allowed
 */
export function getDisabledQualities(isDominant) {
    // If not dominant, return empty set (all qualities allowed)
    if (!isDominant) {
        return new Set();
    }
    // If dominant, disable minor, minorMajor, diminished, augmented, and power qualities
    // Rationale: Dominant chords are major-based by definition in jazz/popular music theory
    // Power chords have no 3rd or 7th, so dominant voicing is meaningless
    return new Set(["minor", "minorMajor", "diminished", "augmented", "power"]);
}
/**
 * Determines which extension options should be disabled based on chord context.
 *
 * **Music Theory Rules:**
 *
 * 1. **Dominant Chord Requirements:**
 *    - Dominant chords MUST have a 7th or higher extension (7, 9, 11, or 13)
 *    - The "-" (no extension) and "6" options are disabled for dominant chords
 *    - Rationale: A dominant chord by definition has a minor 7th
 *
 * 2. **Dominant Sus Restrictions:**
 *    - Dominant sus2 chords: Cannot have 9, 11, or 13 extensions (conflicts with sus2)
 *    - Dominant sus4 chords: Cannot have 11 or 13 extensions (conflicts with sus4)
 *    - Rationale: Sus chords replace the third, which affects available extensions
 *
 * 3. **Non-Dominant Sus Restrictions:**
 *    - Non-dominant sus chords can only have "-" (no extension)
 *    - All other extensions (6, 7, 9, 11, 13) are disabled
 *    - Rationale: Sus chords without dominant function are typically triads
 *
 * 4. **MinorMajor Requirements:**
 *    - MinorMajor chords MUST have a 7th or higher extension
 *    - The "-" (no extension) and "6" options are disabled
 *    - Rationale: MinorMajor by definition has a major 7th
 *
 * 5. **Non-Dominant Diminished Restrictions:**
 *    - Non-dominant diminished chords cannot have 11 or 13 extensions
 *    - Rationale: Diminished chords typically use 7 or 9 extensions only
 *
 * 6. **Non-Dominant Augmented Restrictions:**
 *    - Non-dominant augmented chords can have "-" (triad), "7", or "9" extensions
 *    - Extensions "6", "11", and "13" are disabled
 *    - Rationale: Augmented chords are typically triads (aug), seventh chords (aug7),
 *      or ninth chords (aug9). Higher extensions are uncommon.
 *
 * @param quality - The chord quality from the chord qualities data file. Valid values
 *                  include "major", "minor", "diminished", "augmented", "sus2", "sus4",
 *                  "minorMajor".
 * @param isDominant - Whether the chord is marked as dominant. Dominant chords have
 *                     a major third and minor seventh.
 *
 * @returns A Set containing the extension values that should be disabled in the UI.
 *          The returned set depends on the combination of quality and dominant state.
 *          Returns an empty Set only for standard major/minor chords that are not
 *          dominant (all extensions allowed).
 *
 * @example
 * // Dominant major chord - must have 7th or higher
 * getDisabledExtensions("major", true)
 * // Returns: Set(["-", "6"]) - no extension and 6 are disabled
 *
 * @example
 * // Non-dominant major chord - all extensions allowed
 * getDisabledExtensions("major", false)
 * // Returns: Set([]) - can be C, C6, C7, C9, C11, or C13
 *
 * @example
 * // Dominant sus2 chord - conflicts with 9, 11, 13
 * getDisabledExtensions("sus2", true)
 * // Returns: Set(["-", "6", "9", "11", "13"]) - only 7 is allowed
 *
 * @example
 * // Dominant sus4 chord - conflicts with 11, 13
 * getDisabledExtensions("sus4", true)
 * // Returns: Set(["-", "6", "11", "13"]) - 7 and 9 are allowed
 *
 * @example
 * // Non-dominant sus2 chord - only triad allowed
 * getDisabledExtensions("sus2", false)
 * // Returns: Set(["6", "7", "9", "11", "13"]) - only "-" is allowed
 *
 * @example
 * // Non-dominant sus4 chord - only triad allowed
 * getDisabledExtensions("sus4", false)
 * // Returns: Set(["6", "7", "9", "11", "13"]) - only "-" is allowed
 *
 * @example
 * // MinorMajor chord - must have 7th or higher
 * getDisabledExtensions("minorMajor", false)
 * // Returns: Set(["-", "6"]) - CmMaj7, CmMaj9, etc. are allowed
 *
 * @example
 * // Non-dominant diminished chord - limited extensions
 * getDisabledExtensions("diminished", false)
 * // Returns: Set(["11", "13"]) - Cdim, Cdim7, Cdim9 are allowed
 *
 * @example
 * // Non-dominant augmented chord - triad, 7th, or 9th allowed
 * getDisabledExtensions("augmented", false)
 * // Returns: Set(["6", "11", "13"]) - Caug, Caug7, and Caug9 are allowed
 *
 * @example
 * // Typical usage: Building a G7sus4 chord (dominant)
 * const disabled = getDisabledExtensions("sus4", true);
 * console.log(disabled.has("-")); // true - must have extension
 * console.log(disabled.has("7")); // false - G7sus4 is allowed
 * console.log(disabled.has("11")); // true - conflicts with sus4
 */
export function getDisabledExtensions(quality, isDominant) {
    const disabled = new Set();
    // Power chords have no extensions (just root + 5th)
    if (quality === "power") {
        return new Set(["6", "7", "9", "11", "13"]);
    }
    // Determine if quality is sus
    const isSus = quality === "sus2" || quality === "sus4";
    // If dominant
    if (isDominant) {
        // Dominant chords must have 7th or higher
        disabled.add("-");
        disabled.add("6");
        // If dominant and sus, also disable 11 and 13
        if (isSus) {
            disabled.add("11");
            disabled.add("13");
        }
        // If dominant and sus2, also disable 9
        if (quality === "sus2") {
            disabled.add("9");
        }
    }
    else {
        // Non-dominant cases
        // If non-dominant and sus, disable all except "-"
        if (isSus) {
            disabled.add("6");
            disabled.add("7");
            disabled.add("9");
            disabled.add("11");
            disabled.add("13");
        }
        // If minorMajor, disable "-" and "6"
        if (quality === "minorMajor") {
            disabled.add("-");
            disabled.add("6");
        }
        // If non-dominant diminished, disable "11" and "13"
        if (quality === "diminished") {
            disabled.add("11");
            disabled.add("13");
        }
        // If non-dominant augmented, allow "-", "7", and "9" only
        if (quality === "augmented") {
            disabled.add("6");
            disabled.add("11");
            disabled.add("13");
        }
    }
    return disabled;
}
/**
 * Get the default extension for a quality when the current extension is invalid.
 *
 * This function determines what extension should be used when a quality change
 * makes the current extension invalid. It's used by the chord editor UI to
 * automatically adjust the extension when the user changes the quality.
 *
 * @param quality - The chord quality
 * @param currentExtension - The currently selected extension
 * @param isDominant - Whether the chord is dominant
 *
 * @returns The extension to use, or null if the current extension is valid
 *
 * @example
 * // Augmented chord with invalid extension "11"
 * getDefaultExtensionForQuality("augmented", "11", false)
 * // Returns: "-" (reset to triad)
 *
 * @example
 * // Augmented chord with valid extension "7"
 * getDefaultExtensionForQuality("augmented", "7", false)
 * // Returns: null (no change needed)
 *
 * @example
 * // MinorMajor chord with invalid extension "-"
 * getDefaultExtensionForQuality("minorMajor", "-", false)
 * // Returns: "7" (minorMajor requires extension)
 */
export function getDefaultExtensionForQuality(quality, currentExtension, isDominant) {
    // Get disabled extensions for this quality
    const disabled = getDisabledExtensions(quality, isDominant);
    // If current extension is valid (not disabled), no change needed
    if (!disabled.has(currentExtension)) {
        return null;
    }
    // Current extension is invalid - determine default
    // MinorMajor requires an extension (7 or higher)
    if (quality === "minorMajor") {
        return "7";
    }
    // Dominant chords require an extension (7 or higher)
    if (isDominant) {
        return "7";
    }
    // For all other cases, default to no extension (triad)
    return "-";
}
